import type { Theme } from "@mui/material";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";

import { DIALOG_WIDTH } from "../../constants";
import type { CloseDialogHandler } from "../../contexts/DialogContext";
import { useUpdateServiceTemplate } from "../../hooks/mutation/update/useUpdateServiceTemplate";
import { useKpis } from "../../hooks/query/useKpis";
import { useServiceTemplate } from "../../hooks/query/useServiceTemplate";
import type { ServiceTemplate } from "../../types";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  description: z.string().trim().min(1, "Description is required"),
  type: z.enum(["saas", "retail", "banking", "internal_it"]),
  kpiIds: z.array(z.string()).min(1, "Select at least one KPI"),
});

type FormValues = z.infer<typeof schema>;

const templateTypeOptions: Array<{
  value: ServiceTemplate["type"];
  label: string;
}> = [
  { value: "saas", label: "SaaS" },
  { value: "retail", label: "Retail" },
  { value: "banking", label: "Banking" },
  { value: "internal_it", label: "Internal IT" },
];

const EditServiceTemplateDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  serviceTemplateId: string;
}> = ({ onDialogClosed, serviceTemplateId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [isFormReady, setIsFormReady] = React.useState(false);

  const templateQuery = useServiceTemplate(serviceTemplateId);
  const { data: kpis = [] } = useKpis();
  const isSystemTemplate = templateQuery.data?.system ?? false;
  const { mutateAsync: updateTemplate, isPending: isUpdating } =
    useUpdateServiceTemplate();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      type: "saas",
      kpiIds: [],
    },
    mode: "onBlur",
  });

  React.useEffect(() => {
    setIsFormReady(false);
  }, [serviceTemplateId]);

  React.useEffect(() => {
    const template = templateQuery.data;
    if (!template) {
      return;
    }

    reset({
      name: template.name,
      description: template.description,
      type: template.type,
      kpiIds: template.kpis.map((kpi) => kpi.id),
    });
    setIsFormReady(true);
  }, [reset, templateQuery.data]);

  const onSubmit = async (data: FormValues) => {
    try {
      if (isSystemTemplate) {
        setSubmitError("System service templates cannot be edited.");
        return;
      }

      setSubmitError(null);
      await updateTemplate({
        serviceTemplateId,
        serviceTemplate: {
          name: data.name.trim(),
          description: data.description.trim(),
          type: data.type,
          kpiIds: data.kpiIds,
        },
      });

      onDialogClosed({
        message: "Service template updated!",
        severity: "success",
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onClose = () => {
    if (isUpdating) {
      return;
    }
    setSubmitError(null);
    onDialogClosed();
  };

  const editDisabled = isUpdating || isSystemTemplate;

  return (
    <Dialog
      open
      key={serviceTemplateId}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg },
      }}
    >
      <StandardDialogTitle
        title="Edit Service Template"
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box
        component="form"
        id="edit-service-template-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Update template details and default KPI mapping.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            {templateQuery.isError && (
              <FormHelperText error sx={{ fontSize: 13, m: 0, width: "100%" }}>
                {templateQuery.error instanceof Error
                  ? templateQuery.error.message
                  : "Could not load service template."}
              </FormHelperText>
            )}

            {!templateQuery.isError && !isFormReady && (
              <Box
                sx={{
                  width: "100%",
                  minHeight: 220,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={22} />
              </Box>
            )}

            <Stack
              spacing={2.25}
              sx={{ width: "100%", display: isFormReady ? "flex" : "none" }}
            >
              {isSystemTemplate && (
                <FormHelperText sx={{ fontSize: 13, m: 0 }}>
                  System templates are read-only.
                </FormHelperText>
              )}

              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}

              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. API Gateway"
                  disabled={editDisabled}
                  error={!!errors.name}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>
                  {errors.name?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Template description"
                  disabled={editDisabled}
                  error={!!errors.description}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl error={!!errors.type}>
                <FormLabel>Type</FormLabel>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      size="small"
                      disabled={editDisabled}
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.value);
                        if (submitError) {
                          setSubmitError(null);
                        }
                      }}
                    >
                      {templateTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.type?.message ?? " "}</FormHelperText>
              </FormControl>

              <FormControl error={!!errors.kpiIds}>
                <FormLabel>Default KPIs</FormLabel>
                <Controller
                  name="kpiIds"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      filterSelectedOptions
                      disabled={editDisabled}
                      options={kpis}
                      value={kpis.filter((kpi) =>
                        (field.value ?? []).includes(kpi.id),
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) => option.name}
                      onChange={(_, nextOptions) => {
                        field.onChange(
                          nextOptions.map((option) => option.id),
                        );
                        if (submitError) {
                          setSubmitError(null);
                        }
                      }}
                      ListboxProps={{
                        sx: { p: 0 },
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            size="small"
                            color="primary"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox checked={selected} />
                          <ListItemText
                            primary={option.name}
                            secondary={option.description}
                          />
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder={
                            (field.value ?? []).length === 0
                              ? "Select KPIs"
                              : ""
                          }
                          error={!!errors.kpiIds}
                        />
                      )}
                    />
                  )}
                />
                <FormHelperText>{errors.kpiIds?.message ?? " "}</FormHelperText>
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText="Save"
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isUpdating}
          disabledReason={
            isSystemTemplate ? "System service templates are read-only." : undefined
          }
          disabled={!templateQuery.data || editDisabled}
        />
      </Box>
    </Dialog>
  );
};

export default EditServiceTemplateDialog;
